/**
 * SettingsSection: one titled block of a Settings page. The title and its
 * explanation sit in a narrow left column and the controls fill the right,
 * so every section on every page shares one rhythm: a hairline on top, a
 * serif title, a muted explanation, then the thing itself. Lists, forms,
 * tables and the prompt editor all render inside this frame.
 *
 * `aside` is for the odd extra line under the explanation (a byline, a
 * link to the API reference); it stays in the left column so the right
 * column holds nothing but the controls.
 *
 * SettingsBlock is the same hairline and padding with no columns, for the
 * states that stand where a section will be once its query settles: a
 * skeleton, an error, an empty state.
 */

interface SettingsSectionProps {
  title: string
  /** A status chip beside the title, e.g. a prompt's live version. */
  badge?: React.ReactNode
  description: React.ReactNode
  aside?: React.ReactNode
  children: React.ReactNode
}

export function SettingsSection({
  title,
  badge,
  description,
  aside,
  children,
}: SettingsSectionProps): React.JSX.Element {
  return (
    <section className="grid gap-6 border-t border-border py-8 md:grid-cols-[minmax(10rem,12.5rem)_minmax(0,1fr)] md:gap-8">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-serif text-base font-medium text-foreground">{title}</h2>
          {badge}
        </div>
        <p className="text-pretty text-xs text-muted-foreground">{description}</p>
        {aside}
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  )
}

export function SettingsBlock({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="border-t border-border py-8">{children}</div>
}
